import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import * as React from 'react';
import { useErrorMessages } from '../../../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../../../models/Logging';
import {
	invalidateAllContactFilterQueries,
	useContactTagsUpdateMutation,
} from '../../../../../../../../queries/Contact';
import { CONTACT_QUERY_KEY } from '../../../../../../../../queries/Contact/useContact';

// Custom hook for tags utils with React Query
export const useContactTagsUtils = (contact: Api.IContact, refetchContact: () => void) => {
	// The tagsCollection has the full tag object with color, so we use that if it exists
	const contactTags = contact.tagsCollection ? contact.tagsCollection : contact.tags?.map(name => ({ name })) || [];

	const [tags, setTags] = React.useState<Api.IAccountTag[]>(contactTags);
	const errorMessages = useErrorMessages();
	const { logEvent } = useEventLogging('Tags');
	const queryClient = useQueryClient();

	const updateTagsMutation = useContactTagsUpdateMutation({
		onSuccess: () => {
			invalidateAllContactFilterQueries();
			// Invalidate any contact queries to refetch updated data
			queryClient.invalidateQueries({
				queryKey: [CONTACT_QUERY_KEY, { allowArchived: false, contactId: contact.id, expandHouseholdMembers: false }],
			});
			// Also call the refetchContact function passed from parent
			refetchContact();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Update local state when contact prop changes
	React.useEffect(() => {
		const newContactTags = contact.tagsCollection
			? contact.tagsCollection
			: contact.tags?.map(name => ({ name })) || [];
		setTags(newContactTags);
	}, [contact.tagsCollection, contact.tags]);

	const updateTags = (newTags: Api.IAccountTag[]) => {
		const tagNames = newTags.map(({ name }) => name);
		setTags(newTags); // Optimistic update

		updateTagsMutation.mutate(
			{ contactId: contact.id, tags: tagNames },
			{
				onError: () => {
					// Revert on error
					const revertedTags = contact.tagsCollection
						? contact.tagsCollection
						: contact.tags?.map(name => ({ name })) || [];
					setTags(revertedTags);
				},
			}
		);
	};

	const onRequestAddTag = (accountTag: Api.IAccountTag) => {
		if (
			tags.length &&
			accountTag.name &&
			tags.find(({ name: tag }) => tag.toLocaleLowerCase() === accountTag.name.toLocaleLowerCase())
		) {
			return;
		}
		const newTags: Api.IAccountTag[] = [...tags, accountTag];
		logEvent('AddTag', { count: newTags.length });
		updateTags(newTags);
	};

	const onRequestRemoveTag = (accountTag: Api.IAccountTag) => {
		const newTags = [...tags];
		const index = newTags.findIndex(({ name: tag }) => tag === accountTag.name);
		if (index >= 0) {
			newTags.splice(index, 1);
			logEvent('RemoveTag', { count: newTags.length });
			updateTags(newTags);
		}
	};

	return {
		tags,
		onRequestAddTag,
		onRequestRemoveTag,
		isLoading: updateTagsMutation.isPending,
	};
};
